import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import PPMatchingScoreRing from "./PPMatchingScoreRing"
import PPMatchingProductImage from "./PPMatchingProductImage"
import PPMatchingProductFacts from "./PPMatchingProductFacts"
import PPMatchingAttributeTables from "./PPMatchingAttributeTables"
import {
    getAdditionalProductDetails,
    getMatchSourceLabel,
    isAiMatch,
    LEGACY_ALGORITHM_MATCH_TYPE,
    parseMatchingProductAttributes,
    parseScoringDimensions
} from "../../utils/matching/matchingUtils"

export default function PPMatchingCandidateCard({ candidate, onApprove, approving }) {
    const [t] = useTranslation()
    const [expanded, setExpanded] = useState(false)

    if (!candidate) {
        return null
    }

    const detailsJson = candidate.aiMatchingDetailsJson || candidate.matchingDetailsJson
    const scoringDimensions = parseScoringDimensions(detailsJson)
    const productAttributes = parseMatchingProductAttributes(detailsJson)
    const additionalDetails = getAdditionalProductDetails(candidate, detailsJson)
    const sourceLabel = getMatchSourceLabel(candidate, t("matching.source_algorithm"))
    const isKi = isAiMatch(candidate)
    const displaySourceLabel =
        sourceLabel === LEGACY_ALGORITHM_MATCH_TYPE ? t("matching.source_algorithm") : sourceLabel
    const isDisabled = candidate.approved || candidate.rejected || approving
    const hasConflict = !!candidate.otherApprovedMatch
    const hasAdvancedDetails =
        scoringDimensions.length > 0 ||
        additionalDetails.length > 0 ||
        productAttributes.customerAttributes.length > 0 ||
        productAttributes.foreignAttributes.length > 0 ||
        !!productAttributes.openaiReason ||
        !!productAttributes.staffelInfo?.staffel_reason

    return (
        <div
            className={
                "pp-matching-candidate-card" + (hasConflict ? " pp-matching-candidate-card--conflict" : "")
            }
        >
            <div className={"pp-matching-card-row" + (expanded ? " pp-matching-card-row--expanded" : "")}>
                <PPMatchingProductImage imageUrl={candidate.imageUrl} className="pp-matching-product-img--candidate" />
                <div className="pp-matching-card-row__content">
                    <div className="d-flex align-items-center flex-wrap mb-2">
                        <span
                            className={
                                "badge me-2 pp-matching-source-badge " + (isKi ? "badge-info" : "badge-secondary")
                            }
                        >
                            {displaySourceLabel}
                        </span>
                        {hasConflict && (
                            <span
                                className="pp-matching-conflict-icon me-2"
                                title={t("matching.other_approved_hint", {
                                    match: candidate.otherApprovedMatch
                                })}
                            >
                                <i className="fas fa-exclamation-triangle" />
                            </span>
                        )}
                        {candidate.approved && (
                            <span className="badge badge-success me-1">{t("matching.approved")}</span>
                        )}
                        {candidate.rejected && (
                            <span className="badge badge-danger me-1">{t("matching.rejected")}</span>
                        )}
                    </div>

                    <PPMatchingProductFacts product={candidate} />

                    <div className="pp-matching-candidate-actions mt-2">
                        {hasAdvancedDetails && (
                            <button
                                type="button"
                                className="btn btn-link btn-sm p-0 pp-matching-candidate-actions__details"
                                onClick={() => setExpanded(!expanded)}
                            >
                                {expanded ? t("matching.hide_details") : t("matching.show_details")}
                                <i className={"fas ms-1 fa-angle-" + (expanded ? "up" : "down")} />
                            </button>
                        )}
                        <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            disabled={isDisabled}
                            onClick={() => onApprove(candidate.matchId)}
                        >
                            {approving ? t("matching.approving") : t("matching.assign")}
                        </button>
                    </div>

                    {hasAdvancedDetails && expanded && (
                        <div className="mt-2 border-top pt-2 pp-matching-details-panel">
                            <PPMatchingAttributeTables
                                customerAttributes={productAttributes.customerAttributes}
                                foreignAttributes={productAttributes.foreignAttributes}
                                openaiReason={productAttributes.openaiReason}
                                staffelInfo={productAttributes.staffelInfo}
                            />
                            {scoringDimensions.length > 0 && (
                                <table className="table table-sm table-bordered pp-matching-dimension-table mb-3">
                                    <thead>
                                        <tr>
                                            <th>{t("matching.dimension")}</th>
                                            <th>{t("matching.score")}</th>
                                            <th>
                                                {t("matching.weight")}
                                                <i
                                                    className="fas fa-info-circle ms-1 text-muted pp-matching-weight-hint"
                                                    title={t("matching.weight_hint")}
                                                />
                                            </th>
                                            <th>{t("matching.own_value")}</th>
                                            <th>{t("matching.foreign_value")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {scoringDimensions.map((dim) => (
                                            <tr key={dim.name}>
                                                <td className="pp-matching-detail-cell" title={dim.name}>
                                                    {dim.name}
                                                </td>
                                                <td>{dim.score}%</td>
                                                <td>{dim.weight}</td>
                                                <td
                                                    className="pp-matching-detail-cell pp-matching-detail-cell--value"
                                                    title={dim.customerShopValue}
                                                >
                                                    {dim.customerShopValue}
                                                </td>
                                                <td
                                                    className="pp-matching-detail-cell pp-matching-detail-cell--value"
                                                    title={dim.foreignShopValue}
                                                >
                                                    {dim.foreignShopValue}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                            {additionalDetails.length > 0 && (
                                <ul className="pp-matching-additional-details mb-0">
                                    {additionalDetails.map(({ key, label, value }) => (
                                        <li key={key} title={`${label}: ${value}`}>
                                            <strong>{label}:</strong>
                                            <span className="pp-matching-detail-value">{value}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
                <div className="pp-matching-card-row__score">
                    <PPMatchingScoreRing score={candidate.score} />
                </div>
            </div>
        </div>
    )
}
